import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { signIn, signInWithGoogle, resendVerification, refreshVerification, signOut } from "../lib/dataStore";
import { toast } from "sonner";
import { MailWarning, Info } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get("redirect") || "/";
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [errorNotice, setErrorNotice] = useState(null);

  const handleGoogleSignIn = async () => {
    setBusy(true);
    setErrorNotice(null);
    try {
      await signInWithGoogle();
      toast.success("Welcome back!");
      nav(redirectPath);
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
        toast.error(err.message || "Google sign in failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrorNotice(null);
    try {
      const u = await signIn(form);
      if (u._needsVerification) {
        setPending(u.email);
        toast.error("Please verify your email before continuing");
      } else {
        toast.success("Welcome back");
        nav(redirectPath);
      }
    } catch (err) {
      const msg = err.message || "Sign in failed";
      if (msg.includes("Firebase Console") || msg.includes("operation-not-allowed") || msg.includes("disabled")) {
        setErrorNotice(msg);
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8">
        <div className="max-w-md text-center border border-border p-10 bg-card">
          <MailWarning className="w-12 h-12 text-terracotta mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-2">Verify your email</h1>
          <p className="text-muted-foreground mb-6">
            <strong>{pending}</strong> hasn't been verified yet. Please open the verification link we emailed you, then click "I've verified" below.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              data-testid="ive-verified"
              onClick={async () => {
                const ok = await refreshVerification();
                if (ok) { toast.success("Verified! Signing you in…"); nav("/"); }
                else toast.error("Still not verified. Please click the link in your inbox.");
              }}
              className="px-5 py-2 rounded-full bg-terracotta text-primary-foreground"
            >
              I've verified
            </button>
            <button
              data-testid="resend-verification"
              onClick={async () => { try { await resendVerification(); toast.success("Verification email re-sent"); } catch { toast.error("Could not resend"); } }}
              className="px-5 py-2 rounded-full border border-border"
            >
              Resend email
            </button>
          </div>
          <button
            onClick={async () => { await signOut(); setPending(null); }}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            Use a different account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="/images/login_bag.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" alt="Handcrafted leather" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-moss/40" />
        <div className="absolute bottom-10 left-10 right-10 text-primary-foreground">
          <div className="overline mb-2 opacity-80">Lineacraft India</div>
          <h2 className="font-serif text-4xl">Welcome to the studio.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-5">
          <div>
            <div className="overline text-terracotta mb-1">Sign In</div>
            <h1 className="font-serif text-4xl">Welcome back</h1>
            <p className="text-sm text-muted-foreground mt-2">Sign in to view orders and track shipments.</p>
          </div>

          {errorNotice && (
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-400 space-y-1">
              <div className="font-semibold flex items-center gap-1.5">
                <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                Firebase Auth Note
              </div>
              <p>{errorNotice}</p>
            </div>
          )}

          <button
            type="button"
            data-testid="login-google-button"
            disabled={busy}
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-full border border-border bg-card hover:bg-muted font-medium text-sm transition shadow-xs disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"/>
              <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"/>
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative flex items-center justify-center my-3">
            <div className="border-t border-border w-full"></div>
            <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">or with email</span>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="block text-sm mb-1.5">Email</span>
              <input data-testid="login-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-muted/50 border border-border px-4 py-3 rounded-md outline-none focus:border-foreground" />
            </label>
            <label className="block">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm">Password</span>
                <Link data-testid="goto-forgot-password" to="/forgot-password" className="text-xs text-terracotta hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input data-testid="login-password" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-muted/50 border border-border px-4 py-3 rounded-md outline-none focus:border-foreground" />
            </label>
            <button data-testid="login-submit" disabled={busy} className="w-full py-3 rounded-full bg-foreground text-background hover:bg-terracotta disabled:opacity-50">{busy ? "Signing in…" : "Sign In with Email"}</button>
            <div className="text-sm text-center text-muted-foreground pt-1">
              New here? <Link data-testid="goto-signup" to="/signup" className="text-terracotta underline underline-offset-4">Create an account</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
